// src/components/shared/AtomicUpload.tsx

import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  IconButton,
  Stack,
  Alert,
  Chip,
} from '@mui/material';
import {
  Upload as UploadIcon,
  X as CloseIcon,
  Image as ImageIcon,
  FileText,
  CheckCircle,
} from 'lucide-react';
import imageCompression from 'browser-image-compression';

interface AtomicUploadProps {
  onFilesReady: (files: File[]) => void;
  maxFiles?: number;
  accept?: string;
  maxSize?: number;
  type?: 'images' | 'documents';
  label?: string;
}

interface ProcessedFile {
  id: string;
  originalFile: File;
  processedFile: File;
  progress: number;
  status: 'processing' | 'ready' | 'error';
  originalSize: number;
  compressedSize: number;
  error?: string;
}

export const AtomicUpload: React.FC<AtomicUploadProps> = ({
  onFilesReady,
  maxFiles = 5,
  accept = '*/*',
  maxSize = 50 * 1024 * 1024,
  type = 'images',
  label,
}) => {
  const [processedFiles, setProcessedFiles] = useState<ProcessedFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isImageType = (file: File): boolean => {
    return file.type.startsWith('image/') || 
           file.name.toLowerCase().endsWith('.heic') ||
           file.name.toLowerCase().endsWith('.heif');
  };

  const compressImage = async (file: File, fileId: string): Promise<File> => {
    if (!isImageType(file)) {
      return file; // Return original file if not an image
    }

    const options = {
      maxSizeMB: 2,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
      fileType: 'image/jpeg',
    };

    try {
      // Update progress to show compression starting
      setProcessedFiles(prev => prev.map(pf => 
        pf.id === fileId ? { ...pf, progress: 10, status: 'processing' as const } : pf
      ));

      const compressedFile = await imageCompression(file, options);
      
      // Preserve the original filename properly
      const originalName = file.name.split('.')[0];
      const compressedWithName = new File(
        [compressedFile], 
        `${originalName}.jpg`, 
        { type: 'image/jpeg' }
      );

      // Update progress to show compression complete
      setProcessedFiles(prev => prev.map(pf => 
        pf.id === fileId ? { 
          ...pf, 
          progress: 100, 
          status: 'ready' as const,
          processedFile: compressedWithName,
          compressedSize: compressedWithName.size
        } : pf
      ));

      console.log(`Image compressed: ${(file.size / 1024 / 1024).toFixed(2)}MB → ${(compressedWithName.size / 1024 / 1024).toFixed(2)}MB`);
      return compressedWithName;
    } catch (error) {
      console.warn('Image compression failed, using original:', error);
      
      // Update to show error but still use original file
      setProcessedFiles(prev => prev.map(pf => 
        pf.id === fileId ? { 
          ...pf, 
          progress: 100, 
          status: 'ready' as const,
          processedFile: file,
          compressedSize: file.size,
          error: 'Compression failed, using original'
        } : pf
      ));
      
      return file;
    }
  };

  const processFiles = async (files: File[]) => {
    setIsProcessing(true);
    setError(null);

    // Initialize processed files
    const initialProcessedFiles: ProcessedFile[] = files.map(file => ({
      id: Date.now() + Math.random().toString(),
      originalFile: file,
      processedFile: file,
      progress: 0,
      status: 'processing' as const,
      originalSize: file.size,
      compressedSize: file.size,
    }));

    setProcessedFiles(prev => [...prev, ...initialProcessedFiles]);

    try {
      // Process each file
      const processPromises = initialProcessedFiles.map(async (pf) => {
        if (pf.originalFile.size > maxSize) {
          setProcessedFiles(prev => prev.map(prevPf => 
            prevPf.id === pf.id ? { 
              ...prevPf, 
              status: 'error' as const, 
              error: `File exceeds ${maxSize / 1024 / 1024}MB limit` 
            } : prevPf
          ));
          return null;
        }

        try {
          const processedFile = await compressImage(pf.originalFile, pf.id);
          return processedFile;
        } catch (error) {
          setProcessedFiles(prev => prev.map(prevPf => 
            prevPf.id === pf.id ? { 
              ...prevPf, 
              status: 'error' as const, 
              error: 'Processing failed' 
            } : prevPf
          ));
          return null;
        }
      });

      const results = await Promise.all(processPromises);
      const successfulFiles = results.filter((file): file is File => file !== null);
      
      // Notify parent component with ready files
      if (successfulFiles.length > 0) {
        onFilesReady(successfulFiles);
      }

    } catch (error) {
      setError('Failed to process files');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    
    if (files.length + processedFiles.length > maxFiles) {
      setError(`Maximum ${maxFiles} files allowed`);
      return;
    }

    processFiles(files);
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (fileId: string) => {
    setProcessedFiles(prev => {
      const updated = prev.filter(pf => pf.id !== fileId);
      // Update parent with remaining ready files
      const readyFiles = updated
        .filter(pf => pf.status === 'ready')
        .map(pf => pf.processedFile);
      onFilesReady(readyFiles);
      return updated;
    });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusColor = (status: ProcessedFile['status']) => {
    switch (status) {
      case 'processing': return 'primary';
      case 'ready': return 'success';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (file: ProcessedFile) => {
    switch (file.status) {
      case 'processing': return `Processing... ${file.progress}%`;
      case 'ready': 
        if (file.originalSize !== file.compressedSize) {
          const savings = Math.round(((file.originalSize - file.compressedSize) / file.originalSize) * 100);
          return `Ready (${savings}% smaller)`;
        }
        return 'Ready';
      case 'error': return file.error || 'Error';
      default: return '';
    }
  };

  return (
    <Box>
      {/* File Input Button */}
      <input
        ref={fileInputRef}
        type="file"
        multiple={maxFiles > 1}
        accept={accept}
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      <Button
        variant="outlined"
        startIcon={<UploadIcon />}
        onClick={() => fileInputRef.current?.click()}
        disabled={processedFiles.length >= maxFiles || isProcessing}
        fullWidth
        sx={{ mb: 2 }}
      >
        {label || `Select ${type === 'images' ? 'Images' : 'Documents'}`}
      </Button>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* File List */}
      {processedFiles.length > 0 && (
        <Stack spacing={2} sx={{ mb: 2 }}>
          {processedFiles.map((file) => (
            <Box
  key={file.id}
  sx={{
    border: '1px solid',
    borderColor: file.status === 'error' ? 'error.main' : 'divider',
    borderRadius: 1,
    p: 2,
    bgcolor: file.status === 'ready' ? 'rgba(76, 175, 80, 0.1)' : 'background.paper', // Subtle green tint instead of bright green
    opacity: file.status === 'ready' ? 1 : 0.8,
  }}
>
              <Stack direction="row" alignItems="center" spacing={2}>
                {isImageType(file.originalFile) ? <ImageIcon size={24} /> : <FileText size={24} />}
                
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {file.originalFile.name}
                  </Typography>
                  
                  <Stack direction="row" spacing={2} alignItems="center">
<Chip
  label={getStatusText(file)}
  color={file.status === 'ready' ? 'default' : getStatusColor(file.status)} // Use default for ready state
  size="small"
  icon={file.status === 'ready' ? <CheckCircle size={16} /> : undefined}
  sx={file.status === 'ready' ? { 
    bgcolor: 'rgba(76, 175, 80, 0.2)', 
    color: 'success.main' 
  } : {}}
/>
                    
                    {file.originalSize !== file.compressedSize && file.status === 'ready' && (
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(file.originalSize)} → {formatFileSize(file.compressedSize)}
                      </Typography>
                    )}
                  </Stack>
                  
                  {file.status === 'processing' && (
                    <LinearProgress
                      variant="determinate"
                      value={file.progress}
                      sx={{ mt: 1 }}
                    />
                  )}
                </Box>

                <IconButton 
                  size="small" 
                  onClick={() => removeFile(file.id)}
                  disabled={file.status === 'processing'}
                >
                  <CloseIcon size={18} />
                </IconButton>
              </Stack>
            </Box>
          ))}
        </Stack>
      )}

      {/* Summary */}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {processedFiles.length} of {maxFiles} files • 
        {processedFiles.filter(f => f.status === 'ready').length} ready • 
        {processedFiles.filter(f => f.status === 'processing').length} processing
        {maxSize && ` • Max ${maxSize / 1024 / 1024}MB per file`}
      </Typography>

      {processedFiles.filter(f => f.status === 'ready').length > 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Files will be uploaded when you save the item
        </Alert>
      )}
    </Box>
  );
};
export default AtomicUpload;